import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack,
  Typography, Alert, IconButton, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

// 문의하기 — 제목/내용을 받아 REDERI 쪽 GitHub 이슈로 등록. 관리자에게 자동 알림.
export default function InquiryDialog({ open, onClose }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // { url }

  const reset = () => { setTitle(''); setBody(''); setError(null); setDone(null); setSubmitting(false); };
  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const submit = async () => {
    if (!title.trim() || !body.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), body: body.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) throw new Error(data?.error || `요청 실패 (${res.status})`);
      setDone({ url: data.url || null });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContactSupportIcon color="primary" />
        <Box sx={{ flexGrow: 1 }}>문의하기</Box>
        <IconButton onClick={handleClose} size="small" disabled={submitting}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {done ? (
          <Stack spacing={1.5} sx={{ py: 1 }}>
            <Alert severity="success">문의가 접수되었습니다. 담당자(방성원)에게 전달되었어요.</Alert>
            {done.url && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                접수 번호를 확인하려면 담당자에게 문의하세요.
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              REDERI 플랫폼·콘솔에 대한 문의, 요청, 버그 신고를 남겨주세요. 담당자에게 전달됩니다.
            </Typography>
            {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}
            <TextField
              label="제목"
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
              autoFocus
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="내용"
              fullWidth
              multiline
              minRows={5}
              maxRows={12}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={submitting}
              placeholder="문의 내용을 자세히 적어주세요."
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {done ? (
          <Button variant="contained" onClick={handleClose}>닫기</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>취소</Button>
            <Button variant="contained" onClick={submit} disabled={submitting || !title.trim() || !body.trim()}>
              {submitting ? '접수 중…' : '문의 접수'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
